import Phaser from 'phaser'
import { clearPersistentBudgetState } from './EventCenter'
import { getLevelTransitionVideoCacheKey } from './LevelTransitionVideo'
import {
	createInactivityModal as createSharedInactivityModal,
	INACTIVITY_MODAL_TEXTURE_KEY,
	INACTIVITY_MODAL_TEXTURE_PATH,
	type InactivityModal
} from './InactivityModal'

const INTRO_BACKGROUND_KEY = 'intro-background'
const INTRO_BACKGROUND_PATH = 'assets/init.jpg'
const PRE_INTRO_LOGO_KEY = 'pre-intro-logo'
const PRE_INTRO_LOGO_PATH = 'assets/logo-bbva.png'
const NICKNAME_BACKGROUND_KEY = 'nickname-background'
const NICKNAME_BACKGROUND_PATH = 'assets/nick.jpg'
const INTRO_LOGO_KEY = 'intro-logo'
const INTRO_LOGO_PATH = 'assets/logo.png'
const NICKNAME_WINDOW_KEY = 'nickname-window'
const NICKNAME_WINDOW_PATH = 'assets/ventana_apodo.png'
const START_BUTTON_TEXTURE_KEY = 'start-button-image'
const START_BUTTON_TEXTURE_PATH = 'assets/btn-start.png'
const MENU_BACKGROUND_MUSIC_KEY = 'music-menu-fondo'
const MENU_BACKGROUND_MUSIC_PATH = 'assets/musica/fondo.mp3'
const MENU_BACKGROUND_MUSIC_VOLUME = 0.18
const PIXEL_FONT_FAMILY = '"Press Start 2P", system-ui'

const INTRO_VIDEO_PATH = 'assets/intro.mp4'
const LEVEL_TRANSITION_VIDEO_SCENE_KEY = 'level-transition-video'
const AUTO_ADVANCE_DELAY_MS = 10000
const PRE_INTRO_BACKGROUND_COLOR = 0xf7f8f8
const PRE_INTRO_LOGO_WIDTH_PX = 1800
const INTRO_SUBTITLE_TEXT = 'Un juego de BBVA'
const LOGO_INITIAL_WIDTH_PX = 535
const WAITING_START_LOGO_Y_FACTOR = 0.35
const WAITING_START_SUBTITLE_GAP_PX = 48
const WAITING_START_BUTTON_GAP_PX = 128
const LOGO_TOP_Y_FACTOR = 0.13
const LOGO_REDUCTION_FACTOR = 0.3
const TEXT_CENTER_Y_FACTOR = 0.4
const NICKNAME_MAX_LENGTH = 12
const NICKNAME_MIN_LENGTH = 3
const NICKNAME_ALLOWED_CHARACTERS = ' ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.-'.split('')
const INTRO_START_LEVEL_INDEX = 0
const INACTIVITY_WARNING_DELAY_MS = 15000
const INACTIVITY_RESTART_DELAY_MS = 10000
const INACTIVITY_MODAL_DEPTH = 60

type IntroStep = 'pre-intro' | 'waiting-start' | 'nickname-entry' | 'showing-text-1' | 'showing-text-2' | 'done'

export default class Intro extends Phaser.Scene
{
	private background?: Phaser.GameObjects.Image
	private preIntroBackground?: Phaser.GameObjects.Rectangle
	private preIntroLogo?: Phaser.GameObjects.Image
	private logo?: Phaser.GameObjects.Image
	private introSubtitle?: Phaser.GameObjects.Text
	private startButton?: Phaser.GameObjects.Image
	private nicknameStartButton?: Phaser.GameObjects.Image
	private goButton?: Phaser.GameObjects.Container
	private narrativeText?: Phaser.GameObjects.Text
	private nicknameContainer?: Phaser.GameObjects.Container
	private nicknameWindowImage?: Phaser.GameObjects.Image
	private nicknameInputFrame?: Phaser.GameObjects.Rectangle
	private nicknameSlotTexts: Phaser.GameObjects.Text[] = []
	private nicknameCursorIndex = 0
	private nicknameCharacters = Array<string>(NICKNAME_MAX_LENGTH).fill(' ')
	private baseLogoScale = 1
	private currentStep: IntroStep = 'pre-intro'
	private advanceTimer?: Phaser.Time.TimerEvent
	private inputLocked = false
	private resizeHandler?: (gameSize: Phaser.Structs.Size) => void
	private inactivityModal?: InactivityModal
	private inactivityWarningEvent?: Phaser.Time.TimerEvent
	private inactivityRestartEvent?: Phaser.Time.TimerEvent
	private inactivityCountdownEvent?: Phaser.Time.TimerEvent
	private inactivityLastActivityAtMs = 0
	private inactivityEnabled = false
	private backgroundMusic?: Phaser.Sound.BaseSound
	private currentBackgroundMode: 'intro' | 'nickname' = 'intro'
	private readonly firstNarrativeText = `CUANDO VIAJAS POR EL MUNDO,\nLAS COMISIONES PUEDEN APARECER EN CAJEROS,\nPAGOS Y CAMBIOS DE DIVISA QUERIENDO\nQUEDARSE PARTE DE TU PRESUPUESTO.\n¡HA LLEGADO EL MOMENTO DE PLANTARLES CARA!`
	private readonly secondNarrativeText = `PONTE EN LA PIEL DE THE FEE SLAYER Y\nCUMPLE TU MISIÓN: VIAJA POR EL MUNDO Y\nACABA CON LAS COMISIONES QUE\nSE INTERPONGAN EN TU CAMINO.`

	constructor()
	{
		super('intro')
	}

	init()
	{
		this.currentStep = 'pre-intro'
		this.currentBackgroundMode = 'intro'
		this.inputLocked = false
		this.clearAdvanceTimer()
		this.disableInactivityWatchdog()
		this.nicknameCursorIndex = 0
		this.nicknameCharacters = Array<string>(NICKNAME_MAX_LENGTH).fill(' ')
	}

	preload()
	{
		this.load.image(INTRO_BACKGROUND_KEY, INTRO_BACKGROUND_PATH)
		this.load.image(PRE_INTRO_LOGO_KEY, PRE_INTRO_LOGO_PATH)
		this.load.image(NICKNAME_BACKGROUND_KEY, NICKNAME_BACKGROUND_PATH)
		this.load.image(INTRO_LOGO_KEY, INTRO_LOGO_PATH)
		this.load.image(NICKNAME_WINDOW_KEY, NICKNAME_WINDOW_PATH)
		this.load.image(START_BUTTON_TEXTURE_KEY, START_BUTTON_TEXTURE_PATH)
		this.load.image(INACTIVITY_MODAL_TEXTURE_KEY, INACTIVITY_MODAL_TEXTURE_PATH)
		if (!this.cache.audio.exists(MENU_BACKGROUND_MUSIC_KEY))
		{
			this.load.audio(MENU_BACKGROUND_MUSIC_KEY, MENU_BACKGROUND_MUSIC_PATH)
		}

		const introVideoCacheKey = getLevelTransitionVideoCacheKey(INTRO_VIDEO_PATH)
		if (!this.cache.video.exists(introVideoCacheKey))
		{
			this.load.video(introVideoCacheKey, INTRO_VIDEO_PATH, 'canplaythrough', false, false)
		}
	}

	create()
	{
		this.background = this.add.image(0, 0, INTRO_BACKGROUND_KEY)
		this.preIntroBackground = this.add.rectangle(0, 0, 10, 10, PRE_INTRO_BACKGROUND_COLOR)
		this.preIntroBackground.setOrigin(0, 0)
		this.preIntroLogo = this.add.image(0, 0, PRE_INTRO_LOGO_KEY)
		this.logo = this.add.image(0, 0, INTRO_LOGO_KEY)
		this.introSubtitle = this.add.text(0, 0, INTRO_SUBTITLE_TEXT, {
			fontFamily: PIXEL_FONT_FAMILY,
			fontSize: '26px',
			fontStyle: 'bold',
			color: '#ffffff',
			stroke: '#003283',
			strokeThickness: 2,
			align: 'center'
		}).setOrigin(0.5)
			.setShadow(0, 2, '#001133', 2, false, true)
		this.startButton = this.createStartImageButton(() => {
			if (this.currentStep === 'waiting-start' && !this.inputLocked)
			{
				this.startNicknameSequence()
			}
		})

		this.preIntroBackground.setDepth(40)
		this.preIntroLogo.setDepth(41)
		this.logo.setVisible(false)
		this.introSubtitle.setDepth(35)
		this.introSubtitle.setVisible(false)
		this.startButton.setVisible(false)

		this.layoutForCurrentStep()
		this.bindInput()
		this.createInactivityModal()
		this.startBackgroundMusic()

		this.resizeHandler = () => {
			this.layoutForCurrentStep()
		}
		this.scale.on(Phaser.Scale.Events.RESIZE, this.resizeHandler)

		this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
			this.unbindInput()
			this.clearAdvanceTimer()
			this.disableInactivityWatchdog()
			this.stopBackgroundMusic()
			this.destroyInactivityModal()
			this.destroyNicknameUI()
			if (this.resizeHandler)
			{
				this.scale.off(Phaser.Scale.Events.RESIZE, this.resizeHandler)
				this.resizeHandler = undefined
			}
		})
	}

	private startBackgroundMusic()
	{
		if (this.backgroundMusic?.isPlaying)
		{
			return
		}

		if (!this.cache.audio.exists(MENU_BACKGROUND_MUSIC_KEY))
		{
			return
		}

		const existingMusic = this.sound.get(MENU_BACKGROUND_MUSIC_KEY)
		if (existingMusic)
		{
			this.backgroundMusic = existingMusic
			if (!existingMusic.isPlaying)
			{
				existingMusic.play({ loop: true, volume: MENU_BACKGROUND_MUSIC_VOLUME })
			}
			return
		}

		this.backgroundMusic = this.sound.add(MENU_BACKGROUND_MUSIC_KEY, {
			loop: true,
			volume: MENU_BACKGROUND_MUSIC_VOLUME
		})
		this.backgroundMusic.play()
	}

	private stopBackgroundMusic()
	{
		if (this.backgroundMusic)
		{
			this.backgroundMusic.stop()
			this.backgroundMusic.destroy()
			this.backgroundMusic = undefined
			return
		}

		const existingMusic = this.sound.get(MENU_BACKGROUND_MUSIC_KEY)
		if (existingMusic)
		{
			existingMusic.stop()
			existingMusic.destroy()
		}
	}

	private createActionButton(labelText: string): Phaser.GameObjects.Container
	{
		const buttonWidth = 310
		const buttonHeight = 56
		const background = this.add.rectangle(0, 0, buttonWidth, buttonHeight, 0x101214, 0.9)
			.setStrokeStyle(3, 0xf7ca63, 1)
		const label = this.add.text(0, 0, labelText, {
			fontFamily: PIXEL_FONT_FAMILY,
			fontSize: '22px',
			fontStyle: 'bold',
			color: '#f8f2d6'
		}).setOrigin(0.5)

		const container = this.add.container(0, 0, [background, label])
		container.setSize(buttonWidth, buttonHeight)
		container.setDepth(20)
		return container
	}

	private createStartImageButton(onClick?: () => void): Phaser.GameObjects.Image
	{
		const button = this.add.image(0, 0, START_BUTTON_TEXTURE_KEY)
		button.setDisplaySize(475, 124)
		button.setDepth(26)
		button.setAlpha(1)
		button.setInteractive({ useHandCursor: true })

		if (onClick)
		{
			button.on('pointerdown', () => {
				onClick()
			})
		}

		return button
	}

	private createGoButton()
	{
		if (this.goButton)
		{
			return
		}

		this.goButton = this.createActionButton('GO')
		this.goButton.setAlpha(0)
		this.goButton.setDepth(26)
		this.goButton.setInteractive(
			new Phaser.Geom.Rectangle(-155, -28, 310, 56),
			Phaser.Geom.Rectangle.Contains
		)
		this.goButton.on('pointerdown', () => {
			if (this.currentStep === 'showing-text-2' && !this.inputLocked)
			{
				this.startLevelOne()
			}
		})

		this.layoutForCurrentStep()
		this.tweens.add({
			targets: this.goButton,
			alpha: 1,
			duration: 240,
			ease: 'Sine.easeOut'
		})
	}

	private hideGoButton(onComplete?: () => void)
	{
		if (!this.goButton)
		{
			onComplete?.()
			return
		}

		const button = this.goButton
		this.goButton = undefined
		button.disableInteractive()
		this.tweens.add({
			targets: button,
			y: button.y + 50,
			alpha: 0,
			duration: 320,
			ease: 'Sine.easeIn',
			onComplete: () => {
				button.destroy()
				onComplete?.()
			}
		})
	}

	private bindInput()
	{
		this.input.keyboard?.on('keydown', this.handleKeyDown, this)
		this.input.on('pointerdown', this.handlePointerDown, this)
	}

	private unbindInput()
	{
		this.input.keyboard?.off('keydown', this.handleKeyDown, this)
		this.input.off('pointerdown', this.handlePointerDown, this)
	}

	private handleKeyDown(event: KeyboardEvent)
	{
		this.trackActivity()

		if (this.inputLocked)
		{
			return
		}

		if (this.currentStep === 'pre-intro')
		{
			this.showIntroStartScreen()
			return
		}

		if (this.currentStep === 'nickname-entry')
		{
			this.handleNicknameInput(event)
			return
		}

		switch (this.currentStep)
		{
			case 'waiting-start':
				this.startNicknameSequence()
				break
			case 'showing-text-1':
				this.transitionToSecondNarrative()
				break
			case 'showing-text-2':
				this.startLevelOne()
				break
			default:
				break
		}
	}

	private handlePointerDown()
	{
		this.trackActivity()

		if (this.inputLocked)
		{
			return
		}

		if (this.currentStep === 'pre-intro')
		{
			this.showIntroStartScreen()
			return
		}

		switch (this.currentStep)
		{
			case 'waiting-start':
				if (this.startButton)
				{
					break
				}
				this.startNicknameSequence()
				break
			case 'showing-text-1':
				this.transitionToSecondNarrative()
				break
			case 'showing-text-2':
				this.startLevelOne()
				break
			default:
				break
		}
	}

	private showIntroStartScreen()
	{
		if (this.currentStep !== 'pre-intro')
		{
			return
		}

		this.inputLocked = true
		this.currentStep = 'waiting-start'
		this.logo?.setVisible(true)
		this.startButton?.setVisible(true)
		this.layoutForCurrentStep()

		if (this.preIntroBackground)
		{
			this.tweens.add({
				targets: this.preIntroBackground,
				alpha: 0,
				duration: 180,
				ease: 'Sine.easeOut',
				onComplete: () => {
					this.preIntroBackground?.destroy()
					this.preIntroBackground = undefined
				}
			})
		}

		if (this.preIntroLogo)
		{
			this.tweens.add({
				targets: this.preIntroLogo,
				alpha: 0,
				y: this.preIntroLogo.y - 16,
				duration: 180,
				ease: 'Sine.easeOut',
				onComplete: () => {
					this.preIntroLogo?.destroy()
					this.preIntroLogo = undefined
					this.inputLocked = false
				}
			})
			return
		}

		this.inputLocked = false
	}

	private startNicknameSequence()
	{
		if (!this.logo)
		{
			return
		}

		this.currentStep = 'nickname-entry'
		this.currentBackgroundMode = 'nickname'
		this.inputLocked = true
		this.clearAdvanceTimer()
		this.swapBackgroundByMode()

		const logoTargetY = this.scale.height * LOGO_TOP_Y_FACTOR
		const logoTargetScale = this.baseLogoScale
		this.tweens.add({
			targets: this.logo,
			y: logoTargetY,
			scaleX: logoTargetScale,
			scaleY: logoTargetScale,
			duration: 700,
			ease: 'Sine.easeInOut'
		})

		if (this.startButton)
		{
			if (this.introSubtitle)
			{
				const subtitle = this.introSubtitle
				this.introSubtitle = undefined
				this.tweens.add({
					targets: subtitle,
					y: subtitle.y + 30,
					alpha: 0,
					duration: 320,
					ease: 'Sine.easeIn',
					onComplete: () => {
						subtitle.destroy()
					}
				})
			}

			this.tweens.add({
				targets: this.startButton,
				y: this.startButton.y + 50,
				alpha: 0,
				duration: 320,
				ease: 'Sine.easeIn',
				onComplete: () => {
					this.startButton?.disableInteractive()
					this.startButton?.destroy()
					this.startButton = undefined
				}
			})
		}

		this.time.delayedCall(520, () => {
			this.showNicknameUI()
			this.enableInactivityWatchdog()
			this.inputLocked = false
		})
	}

	private createInactivityModal()
	{
		this.inactivityModal = createSharedInactivityModal(this, {
			depth: INACTIVITY_MODAL_DEPTH,
			fontFamily: PIXEL_FONT_FAMILY
		})
	}

	private destroyInactivityModal()
	{
		this.inactivityModal?.destroy()
		this.inactivityModal = undefined
	}

	private enableInactivityWatchdog()
	{
		this.inactivityEnabled = true
		this.resetInactivityTimers()
	}

	private disableInactivityWatchdog()
	{
		this.inactivityEnabled = false
		this.hideInactivityModal()
		this.clearInactivityTimers()
	}

	private trackActivity()
	{
		if (!this.inactivityEnabled)
		{
			return
		}

		const now = this.time.now
		if (now - this.inactivityLastActivityAtMs < 120)
		{
			return
		}

		this.inactivityLastActivityAtMs = now
		this.hideInactivityModal()
		this.resetInactivityTimers()
	}

	private resetInactivityTimers()
	{
		if (!this.inactivityEnabled)
		{
			return
		}

		this.clearInactivityTimers()
		this.inactivityWarningEvent = this.time.delayedCall(INACTIVITY_WARNING_DELAY_MS, () => {
			this.showInactivityModal()

			this.inactivityRestartEvent = this.time.delayedCall(INACTIVITY_RESTART_DELAY_MS, () => {
				this.restartIntroToFirstScreen()
			})

			this.startInactivityCountdown()
		})
	}

	private clearInactivityTimers()
	{
		if (this.inactivityWarningEvent)
		{
			this.inactivityWarningEvent.remove(false)
			this.inactivityWarningEvent = undefined
		}

		if (this.inactivityRestartEvent)
		{
			this.inactivityRestartEvent.remove(false)
			this.inactivityRestartEvent = undefined
		}

		if (this.inactivityCountdownEvent)
		{
			this.inactivityCountdownEvent.remove(false)
			this.inactivityCountdownEvent = undefined
		}
	}

	private startInactivityCountdown()
	{
		if (this.inactivityCountdownEvent)
		{
			this.inactivityCountdownEvent.remove(false)
			this.inactivityCountdownEvent = undefined
		}

		this.updateInactivityCountdown()

		this.inactivityCountdownEvent = this.time.addEvent({
			delay: 200,
			loop: true,
			callback: () => {
				this.updateInactivityCountdown()
			}
		})
	}

	private updateInactivityCountdown()
	{
		if (!this.inactivityRestartEvent)
		{
			this.inactivityModal?.setCountdownSeconds(null)
			return
		}

		const remainingSeconds = Math.max(0, Math.ceil(this.inactivityRestartEvent.getRemaining() / 1000))
		this.inactivityModal?.setCountdownSeconds(remainingSeconds)
	}

	private showInactivityModal()
	{
		this.inactivityModal?.show()
	}

	private hideInactivityModal()
	{
		this.inactivityModal?.setCountdownSeconds(null)
		this.inactivityModal?.hide()
	}

	private restartIntroToFirstScreen()
	{
		this.currentStep = 'waiting-start'
		this.inputLocked = true
		this.nicknameCursorIndex = 0
		this.nicknameCharacters = Array<string>(NICKNAME_MAX_LENGTH).fill(' ')
		this.disableInactivityWatchdog()
		this.scene.stop('game')
		this.scene.stop('ui')
		this.scene.stop('logo-overlay')
		this.scene.restart()
	}

	private showNicknameUI()
	{
		if (this.nicknameContainer)
		{
			return
		}

		const windowImage = this.add.image(0, 0, NICKNAME_WINDOW_KEY)
		const inputFrame = this.add.rectangle(0, 70, 556, 108, 0x101214, 0)

		const slotTexts: Phaser.GameObjects.Text[] = []
		const slotSpacing = 40
		const slotStartX = -200
		for (let index = 0; index < NICKNAME_MAX_LENGTH; index += 1)
		{
			const slotText = this.add.text(slotStartX + (index * slotSpacing), 57, '_', {
				fontFamily: PIXEL_FONT_FAMILY,
				fontSize: '38px',
				color: '#ffffff'
			}).setOrigin(0.5)
			slotTexts.push(slotText)
		}

		this.nicknameContainer = this.add.container(0, 0, [windowImage, inputFrame, ...slotTexts])
		this.nicknameContainer.setDepth(24)
		this.nicknameContainer.setAlpha(0)
		this.nicknameContainer.setScale(0.92)

		this.nicknameWindowImage = windowImage
		this.nicknameInputFrame = inputFrame
		this.nicknameSlotTexts = slotTexts

		this.layoutForCurrentStep()
		this.refreshNicknameSlots()

		this.tweens.add({
			targets: this.nicknameContainer,
			alpha: 1,
			scaleX: 1,
			scaleY: 1,
			duration: 260,
			ease: 'Sine.easeOut'
		})

		if (!this.nicknameStartButton)
		{
			this.nicknameStartButton = this.createStartImageButton(() => {
				if (this.currentStep === 'nickname-entry' && !this.inputLocked)
				{
					this.confirmNicknameAndContinue()
				}
			})
			this.nicknameStartButton.setAlpha(0)
			this.tweens.add({
				targets: this.nicknameStartButton,
				alpha: 1,
				duration: 260,
				ease: 'Sine.easeOut'
			})
		}

		this.layoutForCurrentStep()
	}

	private handleNicknameInput(event: KeyboardEvent)
	{
		switch (event.code)
		{
			case 'ArrowUp':
				this.cycleNicknameCharacter(1)
				event.preventDefault()
				break
			case 'ArrowDown':
				this.cycleNicknameCharacter(-1)
				event.preventDefault()
				break
			case 'ArrowLeft':
				this.nicknameCursorIndex = Phaser.Math.Wrap(this.nicknameCursorIndex - 1, 0, NICKNAME_MAX_LENGTH)
				this.refreshNicknameSlots()
				event.preventDefault()
				break
			case 'ArrowRight':
				this.nicknameCursorIndex = Phaser.Math.Wrap(this.nicknameCursorIndex + 1, 0, NICKNAME_MAX_LENGTH)
				this.refreshNicknameSlots()
				event.preventDefault()
				break
			case 'Backspace':
			case 'Delete':
				this.nicknameCharacters[this.nicknameCursorIndex] = ' '
				this.refreshNicknameSlots()
				event.preventDefault()
				break
			case 'Enter':
			case 'NumpadEnter':
			case 'Space':
				this.confirmNicknameAndContinue()
				event.preventDefault()
				break
			default:
				break
		}
	}

	private cycleNicknameCharacter(direction: number)
	{
		const currentCharacter = this.nicknameCharacters[this.nicknameCursorIndex]
		const currentIndex = Math.max(0, NICKNAME_ALLOWED_CHARACTERS.indexOf(currentCharacter))
		const nextIndex = Phaser.Math.Wrap(currentIndex + direction, 0, NICKNAME_ALLOWED_CHARACTERS.length)
		this.nicknameCharacters[this.nicknameCursorIndex] = NICKNAME_ALLOWED_CHARACTERS[nextIndex]
		this.refreshNicknameSlots()
	}

	private refreshNicknameSlots()
	{
		this.nicknameSlotTexts.forEach((slotText, index) => {
			const char = this.nicknameCharacters[index]
			slotText.setText(char === ' ' ? '_' : char)

			if (index === this.nicknameCursorIndex)
			{
				slotText.setColor('#ffffff')
				slotText.setScale(1.2)
				return
			}

			slotText.setColor('#d6e7ff')
			slotText.setScale(1)
		})
	}

	private getNicknameValue(): string
	{
		return this.nicknameCharacters.join('').trim()
	}

	private showNicknameValidationError()
	{
		if (!this.nicknameContainer)
		{
			return
		}

		const container = this.nicknameContainer
		const baseX = this.scale.width * 0.5
		this.tweens.killTweensOf(container)
		container.x = baseX
		this.tweens.add({
			targets: container,
			x: baseX + 16,
			duration: 45,
			yoyo: true,
			repeat: 3,
			ease: 'Sine.easeInOut',
			onComplete: () => {
				if (this.nicknameContainer)
				{
					this.nicknameContainer.x = baseX
				}
			}
		})
	}

	private confirmNicknameAndContinue()
	{
		if (this.currentStep !== 'nickname-entry')
		{
			return
		}

		const nickname = this.getNicknameValue()
		if (nickname.length < NICKNAME_MIN_LENGTH)
		{
			this.showNicknameValidationError()
			return
		}

		this.inputLocked = true
		this.disableInactivityWatchdog()
		this.game.registry.set('playerNickname', nickname)
		this.currentBackgroundMode = 'intro'
		this.swapBackgroundByMode()

		if (this.logo)
		{
			this.tweens.add({
				targets: this.logo,
				scaleX: this.baseLogoScale * LOGO_REDUCTION_FACTOR,
				scaleY: this.baseLogoScale * LOGO_REDUCTION_FACTOR,
				duration: 420,
				ease: 'Sine.easeInOut'
			})
		}

		this.hideNicknameUI(() => {
			this.showNarrativeText(this.firstNarrativeText, 'showing-text-1')
		})
	}

	private hideNicknameUI(onComplete: () => void)
	{
		if (this.nicknameStartButton)
		{
			const button = this.nicknameStartButton
			this.nicknameStartButton = undefined
			button.disableInteractive()
			this.tweens.add({
				targets: button,
				y: button.y + 40,
				alpha: 0,
				duration: 250,
				ease: 'Sine.easeIn',
				onComplete: () => {
					button.destroy()
				}
			})
		}

		if (!this.nicknameContainer)
		{
			onComplete()
			return
		}

		const container = this.nicknameContainer
		this.tweens.add({
			targets: container,
			y: container.y + 50,
			alpha: 0,
			duration: 320,
			ease: 'Sine.easeIn',
			onComplete: () => {
				this.destroyNicknameUI()
				onComplete()
			}
		})
	}

	private destroyNicknameUI()
	{
		this.nicknameStartButton?.destroy()
		this.nicknameStartButton = undefined
		this.nicknameContainer?.destroy()
		this.nicknameContainer = undefined
		this.nicknameWindowImage = undefined
		this.nicknameInputFrame = undefined
		this.nicknameSlotTexts = []
	}

	private transitionToSecondNarrative()
	{
		if (this.currentStep !== 'showing-text-1')
		{
			return
		}
		this.clearAdvanceTimer()
		this.hideNarrativeText(() => {
			this.showNarrativeText(this.secondNarrativeText, 'showing-text-2')
		})
	}

	private startLevelOne()
	{
		if (this.currentStep === 'done')
		{
			return
		}

		this.disableInactivityWatchdog()

		this.currentStep = 'done'
		this.inputLocked = true
		this.clearAdvanceTimer()
		this.unbindInput()
		this.stopBackgroundMusic()

		let pendingTransitions = 0
		const tryStartGame = () => {
			pendingTransitions -= 1
			if (pendingTransitions <= 0)
			{
				this.launchIntroVideo()
			}
		}

		if (this.narrativeText)
		{
			pendingTransitions += 1
			this.hideNarrativeText(tryStartGame)
		}

		if (this.goButton)
		{
			pendingTransitions += 1
			this.hideGoButton(tryStartGame)
		}

		if (this.nicknameContainer)
		{
			pendingTransitions += 1
			this.hideNicknameUI(tryStartGame)
		}

		if (pendingTransitions === 0)
		{
			this.launchIntroVideo()
		}
	}

	private launchIntroVideo()
	{
		this.stopBackgroundMusic()
		clearPersistentBudgetState()
		this.scene.start(LEVEL_TRANSITION_VIDEO_SCENE_KEY, {
			nextLevelIndex: INTRO_START_LEVEL_INDEX,
			videoPath: INTRO_VIDEO_PATH,
			sourceSceneKey: this.scene.key
		})
	}

	private showNarrativeText(content: string, step: Extract<IntroStep, 'showing-text-1' | 'showing-text-2'>)
	{
		this.narrativeText?.destroy()

		this.currentStep = step
		this.narrativeText = this.add.text(this.scale.width * 0.5, this.scale.height * TEXT_CENTER_Y_FACTOR, content, {
			fontFamily: PIXEL_FONT_FAMILY,
			fontSize: '24px',
			fontStyle: 'bold',
			color: '#fff',
			align: 'center',
			stroke: '#000',
			strokeThickness: 2,
			// shadow: {
			// 	offsetX: 2,
			// 	offsetY: 2,
			// 	color: '#000000',
			// 	blur: 0,
			// 	stroke: false,
			// 	fill: true
			// }
		})
			.setOrigin(0.5)
			.setAlpha(0)
			.setDepth(25)
		this.narrativeText.setLineSpacing(12)

		this.layoutForCurrentStep()

		this.tweens.add({
			targets: this.narrativeText,
			alpha: 1,
			duration: 260,
			ease: 'Sine.easeOut',
			onComplete: () => {
				if (step === 'showing-text-2')
				{
					this.createGoButton()
				}
				else
				{
					this.hideGoButton()
				}
				this.inputLocked = false
				this.armAutoAdvance()
			}
		})
	}

	private hideNarrativeText(onComplete: () => void)
	{
		if (!this.narrativeText)
		{
			onComplete()
			return
		}

		this.inputLocked = true
		const text = this.narrativeText
		this.narrativeText = undefined
		this.tweens.add({
			targets: text,
			y: text.y + 70,
			alpha: 0,
			duration: 500,
			ease: 'Sine.easeIn',
			onComplete: () => {
				text.destroy()
				onComplete()
			}
		})
	}

	private armAutoAdvance()
	{
		this.clearAdvanceTimer()
		this.advanceTimer = this.time.delayedCall(AUTO_ADVANCE_DELAY_MS, () => {
			if (this.currentStep === 'showing-text-1')
			{
				this.transitionToSecondNarrative()
				return
			}

			if (this.currentStep === 'showing-text-2')
			{
				this.startLevelOne()
			}
		})
	}

	private clearAdvanceTimer()
	{
		if (!this.advanceTimer)
		{
			return
		}

		this.advanceTimer.remove(false)
		this.advanceTimer = undefined
	}

	private layoutForCurrentStep()
	{
		const screenWidth = this.scale.width
		const screenHeight = this.scale.height

		if (this.preIntroBackground)
		{
			this.preIntroBackground.setPosition(0, 0)
			this.preIntroBackground.setSize(screenWidth, screenHeight)
		}

		if (this.preIntroLogo)
		{
			const sourceImage = this.textures.get(PRE_INTRO_LOGO_KEY).getSourceImage() as { width: number, height: number }
			const desiredLogoWidth = Math.min(PRE_INTRO_LOGO_WIDTH_PX, screenWidth * 0.74)
			const logoScale = desiredLogoWidth / sourceImage.width
			this.preIntroLogo.setPosition(screenWidth * 0.5, screenHeight * 0.5)
			this.preIntroLogo.setScale(logoScale)
		}

		if (this.background)
		{
			this.background.setPosition(screenWidth * 0.5, screenHeight * 0.5)
			this.background.setDisplaySize(screenWidth, screenHeight)
			this.background.setVisible(this.currentStep !== 'pre-intro')
		}

		if (this.logo)
		{
			this.logo.setVisible(this.currentStep !== 'pre-intro')

			if (this.currentStep === 'pre-intro')
			{
				return
			}

			const sourceImage = this.textures.get(INTRO_LOGO_KEY).getSourceImage() as { width: number, height: number }
			const desiredLogoWidth = Math.min(LOGO_INITIAL_WIDTH_PX, screenWidth * 0.92)
			this.baseLogoScale = desiredLogoWidth / sourceImage.width

			if (this.currentStep === 'waiting-start')
			{
				this.logo.setPosition(screenWidth * 0.5, screenHeight * WAITING_START_LOGO_Y_FACTOR)
				this.logo.setScale(this.baseLogoScale)
			}
			else if (this.currentStep === 'nickname-entry')
			{
				this.logo.setPosition(screenWidth * 0.5, screenHeight * LOGO_TOP_Y_FACTOR)
				this.logo.setScale(this.baseLogoScale)
			}
			else
			{
				this.logo.setPosition(screenWidth * 0.5, screenHeight * (LOGO_TOP_Y_FACTOR - 0.08) )
				this.logo.setScale(this.baseLogoScale * LOGO_REDUCTION_FACTOR)
			}
		}

		if (this.startButton && this.logo)
		{
			this.startButton.setVisible(this.currentStep !== 'pre-intro')
			if (this.currentStep === 'waiting-start' && this.introSubtitle)
			{
				const logoBottomY = this.logo.y + (this.logo.displayHeight * 0.5)
				const subtitleY = logoBottomY + WAITING_START_SUBTITLE_GAP_PX
				this.startButton.setPosition(screenWidth * 0.5, subtitleY + WAITING_START_BUTTON_GAP_PX)
			}
			else
			{
				this.startButton.setPosition(screenWidth * 0.5, this.logo.y + (this.logo.displayHeight * 0.5) + 100)
			}
		}

		if (this.introSubtitle && this.logo && this.startButton)
		{
			const shouldShowSubtitle = this.currentStep === 'waiting-start'
			this.introSubtitle.setVisible(shouldShowSubtitle)
			if (shouldShowSubtitle)
			{
				this.introSubtitle.setAlpha(1)
				const subtitleY = (this.logo.y + (this.logo.displayHeight * 0.5)) + WAITING_START_SUBTITLE_GAP_PX
				this.introSubtitle.setPosition(screenWidth * 0.5, subtitleY)
			}
		}

		if (this.narrativeText)
		{
			this.narrativeText.setPosition(screenWidth * 0.5, screenHeight * TEXT_CENTER_Y_FACTOR)
		}

		if (this.nicknameContainer)
		{
			this.nicknameContainer.setPosition(screenWidth * 0.5, screenHeight * 0.45)

			if (this.nicknameWindowImage)
			{
				const source = this.textures.get(NICKNAME_WINDOW_KEY).getSourceImage() as { width: number, height: number }
				const fitScale = Math.min(1.12, (screenWidth * 0.92) / source.width, (screenHeight * 0.56) / source.height)
				this.nicknameContainer.setScale(fitScale)
			}
		}

		if (this.nicknameStartButton)
		{
			const nicknameBottomY = this.nicknameContainer
				? this.nicknameContainer.y + ((this.nicknameWindowImage?.displayHeight ?? 0) * 0.5)
				: screenHeight * 0.62
			this.nicknameStartButton.setPosition(screenWidth * 0.5, Math.min(screenHeight - 78, nicknameBottomY + 82))
		}

		if (this.inactivityModal)
		{
			this.inactivityModal.reposition()
		}

		if (this.goButton && this.narrativeText)
		{
			const goButtonY = Math.min(
				screenHeight - 52,
				this.narrativeText.y + (this.narrativeText.height * 0.5) + 56
			)
			this.goButton.setPosition(screenWidth * 0.5, goButtonY)
		}
	}

	private swapBackgroundByMode()
	{
		if (!this.background)
		{
			return
		}

		const targetTexture = this.currentBackgroundMode === 'nickname' ? NICKNAME_BACKGROUND_KEY : INTRO_BACKGROUND_KEY
		if (this.background.texture.key === targetTexture)
		{
			return
		}

		this.background.setTexture(targetTexture)
		this.background.setDisplaySize(this.scale.width, this.scale.height)
	}
}
